import { Button } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import Decimal from "decimal.js";
import { useTranslation } from "react-i18next";
import type { CurrencyPrimitive } from "@/api/cashier";
import { InputFieldBase } from "@/components/forms/input-field-base/InputFieldBase";
import { useAmountInput } from "@/hooks/useAmountInput";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import styles from "./WithdrawAmountInput.module.css";

type WithdrawAmountInputProps = {
  setInputValue?: (amount: Decimal | undefined) => void;
  currency: CurrencyPrimitive;
  disabled?: boolean;
  min?: Decimal;
  max?: Decimal;
  limits?: string;
  onError?: (
    error: "format" | "min" | "max" | "",
    input: string | undefined
  ) => void;
  style?: any;
  id?: string;
  locale: string;
};

export const WithdrawAmountInput = ({
  id,
  min,
  max,
  setInputValue,
  disabled,
  currency,
  onError,
  limits,
  style,
  locale,
}: WithdrawAmountInputProps) => {
  const { t } = useTranslation();
  const { formatAmount } = useFormatAmount();

  const { value, error, handleChange, setFromDecimal } = useAmountInput({
    currency,
    min,
    max,
    maxDecimalPlaces: 2,
    onChange: setInputValue,
    onError,
    locale,
  });

  if (min?.isNaN()) min = undefined;
  if (max?.isNaN()) max = undefined;

  const isMaxSet = typeof max !== "undefined";
  const isMinSet = typeof min !== "undefined";

  const invalidLimits =
    typeof max !== "undefined" && typeof min !== "undefined" && max.lt(min);

  const placeholder = invalidLimits
    ? "0"
    : isMaxSet && isMinSet
      ? `${min}-${max}`
      : isMaxSet
        ? `0-${max}`
        : "0";

  limits =
    limits ??
    t("withdrawals.methods.amount_input.available_balance", {
      amount: !isMaxSet ? "--" : formatAmount(max, currency),
    });

  min = min ?? new Decimal(0);
  max = max ?? new Decimal(Number.MAX_SAFE_INTEGER);

  return (
    <InputFieldBase
      data-testid="withdraw-amount-input-field-base"
      disabled={disabled}
      error={error}
      hint={limits}
      size="lg"
      labelFor="name"
      style={style}
    >
      <input
        data-testid="withdraw-amount-input-field-input"
        id={id}
        type="text"
        inputMode="decimal"
        onChange={(e) => {
          handleChange(`${e.target.value}`);
          // const event = new CustomEvent("WithdrawAmountInputChange", {
          //   bubbles: true,
          // });
          // document.dispatchEvent(event);
        }}
        value={value}
        onWheel={(e) => e.currentTarget.blur()}
        placeholder={placeholder}
        disabled={disabled}
      />
      <span className={clsx(disabled && styles.disabledCurrency)}>
        {currency.code}
      </span>
      {isMaxSet && (
        <Button
          data-testid="withdraw-amount-input-field-max-button"
          disabled={disabled}
          className={clsx(styles.maxButton)}
          variant="ps-btn-primary"
          size="ps-btn-sm"
          type="button"
          onClick={() => setFromDecimal(max)}
        >
          {t("withdrawals.methods.amount_input.max")}
        </Button>
      )}
    </InputFieldBase>
  );
};
